import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuCalendarRange, LuHistory, LuPlane } from 'react-icons/lu';
import { BLOCK_TITLE, TILE } from '../../ProfilePage/components/ProfileUi';
import useLeaveText from '../../LeaveRequests/parts/leaveText';
import { daysFromToday } from '../leaveSummary';
import useSmoText from '../smoText';

const BALANCE_ITEMS = ['entitlement', 'used', 'reserved', 'remaining'];

/** Value with a small caption under it. */
const Stat = ({ name, value, hint, tone = 'tw-text-slate-900 dark:tw-text-white' }) => (
  <div className={`${TILE} tw-min-w-0 tw-px-3 tw-py-2.5`}>
    <dd className={`tw-truncate tw-text-lg tw-font-semibold tw-leading-tight tw-tabular-nums ${tone}`}>{value}</dd>
    <dt className="tw-mt-1 tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{name}</dt>
    {hint && <p className="tw-mt-0.5 tw-truncate tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{hint}</p>}
  </div>
);

Stat.propTypes = { name: PropTypes.string.isRequired, value: PropTypes.node.isRequired, hint: PropTypes.string, tone: PropTypes.string };

/** Day count, plus hours when hourly records exist ("2 gün · 4 saat"). */
export const useDuration = () => {
  const { days, formatNumber } = useLeaveText();
  const { label } = useSmoText();
  return (dayCount, hourCount) => [
    (dayCount > 0 || !hourCount) && days(dayCount),
    hourCount > 0 && label('leaves.hours', { hours: formatNumber(hourCount) }),
  ].filter(Boolean).join(' · ');
};

/** Şu an izinde / son izin / sıradaki izin. */
const TimelineItem = ({ icon: Icon, name, record, tone }) => {
  const { label, locale } = useSmoText();
  const { typeLabel, formatDate } = useLeaveText();
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  let detail = label('leaves.timeline.none');
  if (record) {
    // Şu anki izin bitişiyle, son izin bitişiyle, sıradaki izin başlangıcıyla anlatılır
    const anchor = tone === 'next' ? record.start : record.end;
    const when = tone === 'current'
      ? label('leaves.timeline.until', { date: formatDate(anchor) })
      : `${formatDate(anchor)} · ${relative.format(daysFromToday(anchor), 'day')}`;
    detail = `${typeLabel(record.leaveType)} · ${when}`;
  }
  return (
    <div className={`tw-flex tw-min-w-0 tw-items-start tw-gap-2.5 tw-rounded-2xl tw-px-3 tw-py-2.5 ${tone === 'current' && record ? 'tw-bg-emerald-500/10' : 'tw-bg-slate-500/[0.06] dark:tw-bg-white/[0.04]'}`}>
      <Icon aria-hidden="true" className={`tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 ${tone === 'current' && record ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-slate-400'}`} />
      <div className="tw-min-w-0">
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{name}</p>
        <p className="tw-mt-0.5 tw-break-words tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">
          {detail}
          {record?.status === 'PENDING' && <span className="tw-ml-1.5 tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-text-[11px] tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">{label('leaves.filter.pending')}</span>}
        </p>
      </div>
    </div>
  );
};

TimelineItem.propTypes = { icon: PropTypes.elementType.isRequired, name: PropTypes.string.isRequired, record: PropTypes.object, tone: PropTypes.oneOf(['current', 'last', 'next']).isRequired };

/** Twelve bars of approved leave days by start month. */
const MonthBars = ({ byMonth, year }) => {
  const { label, locale } = useSmoText();
  const { formatNumber } = useLeaveText();
  const duration = useDuration();
  const max = Math.max(...byMonth.map((m) => m.days + m.hours / 8), 0);
  return (
    <ol className="tw-grid tw-grid-cols-12 tw-items-end tw-gap-1 sm:tw-gap-1.5">
      {byMonth.map((month, index) => {
        const name = new Date(year, index, 1).toLocaleDateString(locale, { month: 'short' });
        const size = month.days + month.hours / 8;
        return (
          <li
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            title={label('leaves.monthTitle', { month: name, count: month.count, duration: duration(month.days, month.hours) })}
            className="tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-gap-1"
          >
            <span className="tw-h-4 tw-text-[10px] tw-font-semibold tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{month.count > 0 ? formatNumber(month.days || month.hours) : ''}</span>
            <span className="tw-flex tw-h-20 tw-w-full tw-items-end tw-rounded-md tw-bg-slate-500/[0.06] dark:tw-bg-white/[0.04]">
              <span
                className="tw-w-full tw-rounded-md tw-bg-gradient-to-t tw-from-teal-500 tw-to-cyan-400"
                style={{ height: max > 0 && size > 0 ? `${Math.max(8, (size / max) * 100)}%` : 0 }}
              />
            </span>
            <span className="tw-w-full tw-truncate tw-text-center tw-text-[10px] tw-text-slate-500 dark:tw-text-slate-400">{name}</span>
          </li>
        );
      })}
    </ol>
  );
};

MonthBars.propTypes = { byMonth: PropTypes.array.isRequired, year: PropTypes.number.isRequired };

/**
 * Top of the leave window: the year's annual-leave balance, how often the
 * worker takes leave, the current / last / next leave and how the year's
 * leave spreads over months and types.
 */
const LeaveOverview = ({ summary, timeline, year }) => {
  const { label } = useSmoText();
  const { typeLabel, formatNumber, days } = useLeaveText();
  const duration = useDuration();
  const { balance } = summary;
  return (
    <div className="tw-space-y-4">
      <section className="tw-space-y-2">
        <h3 className={BLOCK_TITLE}>{label('leaves.balanceTitle', { year })}</h3>
        <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
          {BALANCE_ITEMS.map((item) => (
            <Stat
              key={item}
              name={label(`leaves.balance.${item}`)}
              value={days(balance[item])}
              tone={item === 'remaining' ? (balance.remaining <= 0 ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-emerald-600 dark:tw-text-emerald-300') : undefined}
            />
          ))}
        </dl>
      </section>

      <section className="tw-grid tw-gap-2 sm:tw-grid-cols-3">
        <TimelineItem icon={LuPlane} name={label('leaves.timeline.current')} record={timeline.current} tone="current" />
        <TimelineItem icon={LuHistory} name={label('leaves.timeline.last')} record={timeline.last} tone="last" />
        <TimelineItem icon={LuCalendarClock} name={label('leaves.timeline.next')} record={timeline.next} tone="next" />
      </section>

      <section className="tw-space-y-2">
        <h3 className={BLOCK_TITLE}>{label('leaves.frequencyTitle', { year })}</h3>
        <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
          <Stat
            name={label('leaves.frequency.count')}
            value={label('leaves.frequency.countValue', { count: summary.approvedCount })}
            hint={summary.pendingCount || summary.rejectedCount ? label('leaves.frequency.otherStatus', { pending: summary.pendingCount, rejected: summary.rejectedCount }) : undefined}
          />
          <Stat name={label('leaves.frequency.total')} value={duration(summary.totalDays, summary.totalHours)} />
          <Stat
            name={label('leaves.frequency.gap')}
            value={summary.averageGapDays !== null ? label('leaves.frequency.gapValue', { days: formatNumber(summary.averageGapDays) }) : '-'}
          />
          <Stat name={label('leaves.frequency.perMonth')} value={label('leaves.frequency.perMonthValue', { value: formatNumber(summary.perMonth) })} />
        </dl>
      </section>

      {summary.approvedCount > 0 && (
        <section className="tw-grid tw-gap-4 lg:tw-grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="tw-space-y-2">
            <h3 className={BLOCK_TITLE}><LuCalendarRange aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{label('leaves.monthsTitle')}</h3>
            <MonthBars byMonth={summary.byMonth} year={year} />
          </div>
          <div className="tw-space-y-2">
            <h3 className={BLOCK_TITLE}>{label('leaves.typesTitle')}</h3>
            <ul className="tw-space-y-1.5">
              {summary.byType.map((type) => (
                <li key={type.typeKey || '-'} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-slate-500/[0.06] tw-px-3 tw-py-2 tw-text-sm dark:tw-bg-white/[0.04]">
                  <span className="tw-min-w-0 tw-truncate tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{typeLabel(type.typeKey)}</span>
                  <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">
                    {label('leaves.typeValue', { count: type.count, duration: duration(type.days, type.hours) })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
};

LeaveOverview.propTypes = {
  summary: PropTypes.object.isRequired,
  timeline: PropTypes.object.isRequired,
  year: PropTypes.number.isRequired,
};

export default LeaveOverview;
